import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import axios from '../utils/axios';
import { toast } from 'react-hot-toast';
import { Card, CardContent } from '../components/ui/card';
import Input from '../components/ui/input';
import Button from '../components/ui/button';

const BlogPost = () => {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [relatedPosts, setRelatedPosts] = useState([]);
    const [recentPosts, setRecentPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchPost = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const response = await axios.get(`/blog/${slug}`);
            if (response.data.success) {
                setPost(response.data.post);
                setRelatedPosts(response.data.related_posts || []);
                setRecentPosts(response.data.recent_posts || []);
            }
        } catch (error) {
            console.error('Error fetching post:', error);
            setError('Failed to fetch blog post');
            toast.error('Failed to load blog post. Please try again later.');
        } finally {
            setLoading(false);
        }
    }, [slug]);

    useEffect(() => {
        fetchPost();
    }, [fetchPost]);

    return (
        <Layout>
            <div className="container mx-auto px-4 py-8">
                {/* Back to Blog */}
                <div className="mb-8">
                    <Link
                        to="/blog"
                        className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors"
                    >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Back to Blog
                    </Link>
                </div>

                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                ) : error ? (
                    <div className="text-center">
                        <h1 className="text-2xl font-bold text-gray-900 mb-4">Post Not Found</h1>
                        <p className="text-gray-600 mb-6">
                            {error || 'The blog post you are looking for does not exist.'}
                        </p>
                        <Link
                            to="/blog"
                            className="text-blue-600 hover:text-blue-800 font-medium"
                        >
                            ← Back to Blog
                        </Link>
                    </div>
                ) : post ? (
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                        {/* Main Content */}
                        <div className="lg:col-span-8">
                            <Card>
                                <CardContent className="p-8">
                                    {/* Post Header */}
                                    <header className="mb-8">
                                        {post.category && (
                                            <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-semibold rounded-full mb-4 inline-block">
                                                {post.category}
                                            </span>
                                        )}
                                        <h1 className="text-4xl font-bold text-gray-900 mb-4">
                                            {post.title}
                                        </h1>
                                        <div className="flex items-center text-gray-600 mb-6">
                                            <span>By {post.author?.name}</span>
                                            <span className="mx-2">•</span>
                                            <span>{new Date(post.published_at).toLocaleDateString()}</span>
                                            <span className="mx-2">•</span>
                                            <span>{post.reading_time}</span>
                                            <span className="mx-2">•</span>
                                            <span>{post.views_count} views</span>
                                        </div>
                                        {post.excerpt && (
                                            <p className="text-xl text-gray-700 leading-relaxed">
                                                {post.excerpt}
                                            </p>
                                        )}
                                    </header>

                                    {/* Featured Image */}
                                    {post.featured_image && (
                                        <div className="mb-8">
                                            <img
                                                src={`/storage/${post.featured_image}`}
                                                alt={post.title}
                                                className="w-full h-96 object-cover rounded-lg shadow-lg"
                                            />
                                        </div>
                                    )}

                                    {/* Post Content */}
                                    <div className="prose prose-lg max-w-none mb-8">
                                        <div
                                            dangerouslySetInnerHTML={{ __html: post.content }}
                                            className="text-gray-800 leading-relaxed"
                                        />
                                    </div>

                                    {/* Tags */}
                                    {post.tags && post.tags.length > 0 && (
                                        <div className="mb-8">
                                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Tags</h3>
                                            <div className="flex flex-wrap gap-2">
                                                {post.tags.map((tag) => (
                                                    <Link
                                                        key={tag}
                                                        to={`/blog?tag=${tag}`}
                                                        className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full hover:bg-gray-200 transition-colors"
                                                    >
                                                        #{tag}
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Author Info */}
                                    <div className="border-t border-gray-200 pt-8">
                                        <div className="flex items-center">
                                            <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center mr-4">
                                                <span className="text-lg font-bold text-gray-600">
                                                    {post.author?.name?.charAt(0)}
                                                </span>
                                            </div>
                                            <div>
                                                <h4 className="text-lg font-semibold text-gray-900">
                                                    {post.author?.name}
                                                </h4>
                                                <p className="text-gray-600">Author</p>
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Sidebar */}
                        <div className="lg:col-span-4">
                            {/* Recent Posts */}
                            {recentPosts.length > 0 && (
                                <Card className="mb-8">
                                    <CardContent className="p-6">
                                        <h3 className="text-xl font-bold text-gray-900 mb-4">Recent Posts</h3>
                                        <div className="space-y-4">
                                            {recentPosts.map((recentPost) => (
                                                <div key={recentPost.id} className="border-b border-gray-200 pb-4 last:border-0 last:pb-0">
                                                    <Link
                                                        to={`/blog/${recentPost.slug}`}
                                                        className="block hover:text-blue-600 transition-colors"
                                                    >
                                                        <h4 className="font-medium text-gray-900 mb-1">{recentPost.title}</h4>
                                                        <div className="text-sm text-gray-600">
                                                            {new Date(recentPost.published_at).toLocaleDateString()}
                                                        </div>
                                                    </Link>
                                                </div>
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Newsletter Subscription */}
                            <Card>
                                <CardContent className="p-6">
                                    <h3 className="text-xl font-bold text-gray-900 mb-4">Stay Updated</h3>
                                    <p className="text-gray-600 mb-6">
                                        Subscribe to our newsletter to get the latest posts delivered to your inbox.
                                    </p>
                                    <NewsletterSubscription />
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                ) : null}

                {/* Related Posts */}
                {post && relatedPosts.length > 0 && (
                    <div className="mt-12">
                        <h3 className="text-2xl font-bold text-gray-900 mb-6">Related Posts</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {relatedPosts.map((relatedPost) => (
                                <Card key={relatedPost.id} className="hover:shadow-lg transition-shadow">
                                    {relatedPost.featured_image && (
                                        <img
                                            src={`/storage/${relatedPost.featured_image}`}
                                            alt={relatedPost.title}
                                            className="w-full h-48 object-cover"
                                        />
                                    )}
                                    <CardContent className="p-4">
                                        {relatedPost.category && (
                                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full mb-2 inline-block">
                                                {relatedPost.category}
                                            </span>
                                        )}
                                        <h4 className="text-lg font-semibold text-gray-900 mb-2">
                                            <Link
                                                to={`/blog/${relatedPost.slug}`}
                                                className="hover:text-blue-600 transition-colors"
                                            >
                                                {relatedPost.title}
                                            </Link>
                                        </h4>
                                        <p className="text-gray-600 text-sm mb-2 line-clamp-2">
                                            {relatedPost.excerpt}
                                        </p>
                                        <div className="flex items-center text-xs text-gray-500">
                                            <span>{new Date(relatedPost.published_at).toLocaleDateString()}</span>
                                            <span className="mx-2">•</span>
                                            <span>{relatedPost.views_count} views</span>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

// Newsletter Subscription Component
const NewsletterSubscription = () => {
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const response = await axios.post('/newsletter/subscribe', { email, name });
            if (response.data.success) {
                toast.success('Successfully subscribed to newsletter!');
                setEmail('');
                setName('');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to subscribe. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <Input
                    type="text"
                    placeholder="Your name (optional)"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full"
                />
            </div>
            <div>
                <Input
                    type="email"
                    placeholder="Your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full"
                />
            </div>
            <Button
                type="submit"
                disabled={loading}
                className="w-full"
            >
                {loading ? 'Subscribing...' : 'Subscribe'}
            </Button>
        </form>
    );
};

export default BlogPost;
